import { ITaskPrefill } from "../../models/task";
import { TaskServices } from "../../services";

/**
 * What ENQUIRY_EVENT.CREATE_TASK carries (FRO-11391), documented in
 * enquiry-events.constants.ts. `taskId` is the contract; the three ids are a hedge —
 * the Enquiry app posted them before its ticket comment settled on the task id, and
 * they cost nothing to keep accepting.
 */
export interface ICreateTaskPayload {
	taskId?: number | string;
	propertyId?: number | string;
	personId?: number | string;
	userId?: number | string;
}

const toId = (value: number | string): number => {
	const id = Number(value);
	return id > 0 ? id : null;
};

/** A message carrying context is the detail view's "Follow-up task"; a bare one is the list's "+". */
const hasFollowUpContext = (payload: ICreateTaskPayload): boolean =>
	!!(payload && (payload.taskId || payload.propertyId || payload.personId || payload.userId));

/**
 * Resolve what a follow-up seeds its new task with. Given the enquiry's task id the
 * follow-up starts as that task — property, person, demand, assignee, type, source,
 * subject and comment — leaving the agent the new date. Ids on their own carry only
 * what they name.
 *
 * Never rejects: an unreadable task answers an empty prefill, so the action still
 * opens a task form rather than dead-ending.
 */
async function buildFollowUpTaskPrefill(
	taskService: TaskServices,
	payload: ICreateTaskPayload
): Promise<ITaskPrefill> {
	const taskId = toId(payload.taskId);
	if (taskId) return taskService.getPrefillFromTask(taskId);

	const prefill: ITaskPrefill = {};
	const propertyId = toId(payload.propertyId);
	const personId = toId(payload.personId);
	const userId = toId(payload.userId);
	if (propertyId) prefill.ObjectId = propertyId;
	if (personId) prefill.PersonId = personId;
	if (userId) prefill.UserId = userId;

	return prefill;
}

/**
 * Which task drawer a create-task message opens, and with what. Shared so the SPA
 * host and the WinApp webview host — which differ only in how they open a drawer —
 * can't drift on the contract.
 *
 * A follow-up opens the normal task form: the enquiry task types the New Enquiry
 * form is limited to are what sent agents to the Tasks module for a follow-up in
 * the first place. Its prefill is handed over unresolved, so the lookup overlaps
 * the form's own init request instead of delaying the drawer.
 */
export function resolveCreateTaskDrawer(
	taskService: TaskServices,
	payload: ICreateTaskPayload
): { props: object, title: string } {
	if (!hasFollowUpContext(payload)) {
		// The enquiry list's "+": the New Enquiry form (enquiry task types + default), not "New Task".
		return { props: { isEnquiry: true }, title: 'NEW_ENQUIRY' };
	}

	return {
		props: { prefill: buildFollowUpTaskPrefill(taskService, payload) },
		title: 'FOLLOW_UP_TASK'
	};
}
